import type {Dispatch, SetStateAction} from "react";
import { useDebugValue, useMemo, useState} from "react"
import type {Spec} from "./imupdate";
import imupdate from "./imupdate"
import setIn from "lodash/fp/set"
import {get as getPath, toPath} from 'lodash'
import {toBool} from "../kb"
import {isFunction, isObject} from '../util/is-type'
import {arraySetChange, iterableIncludes} from '../util/collections'
import type {AioCheckboxListChangeEvent} from '../components/aio-inputs/AioCheckboxList'

type Path = string | number | Array<string | number>
type KeyOrPath<T> = keyof T | Path
export type DispatchStateAction<S> = Dispatch<SetStateAction<S>>

export class Store<T extends Record<string, any> = Record<string, any>> {
    readonly initialData: T

    constructor(readonly data: T, readonly setData: DispatchStateAction<T>) {
        this.initialData = data
    }

    /** @deprecated I don't think this will even work -- the initialData keeps getting reset */
    clear() {
        if(__DEV__) throw new Error("Store.clear called")
        return this.setData(this.initialData)
    }

    // CustomCommands: https://github.com/kolodny/immutability-helper#adding-your-own-commands
    update($spec: Spec<T>) {
        return this.setData(imupdate($spec))
    }

    shallowMerge(data: Partial<T>) {
        return this.setData(d => ({...d, ...data}))
    }

    set<K extends keyof T>(key: K, value: SetStateAction<T[K]>): void
    set(key: Path, value: SetStateAction<unknown>): void
    set(key: KeyOrPath<T>, value: any) {
        this.setData(setIn(key, isFunction(value) ? value(getPath(this.data, key)) : value))
    }

    push<K extends keyof PickByType<T,any[]>>(key: K, value: ArrayType<T[K]>): void {
        this.set(key, v => [...v, value])
    }

    get<K extends keyof T>(key: K): T[K];
    get<K extends keyof T,D>(key: K, defaultValue: D): T[K]|D;
    get(key: Path, defaultValue?: any): any;
    get(key: KeyOrPath<T>, defaultValue?: any) {
        const value = getPath(this.data, key, defaultValue)
        if(isObject(value) && isFunction((value as any).toJSON)) {
            // Hack fix for phone number formatting
            return value.toJSON()
        }
        return value
    }

    // has(key: KeyPath<T>) {
    //     return hasPath(this.data, key);
    // }

    forward(pathStr: KeyOrPath<T>) {
        // if(typeof this.data !== 'object') throw new Error("Cannot forward primitives");
        // console.log('forwarding!!',pathStr)
        const path = toPath(pathStr)
        const subData = getPath(this.data, path)
        const setter = (subData: any) => this.setData((fullData: T) => {
            if(isFunction(subData)) {
                subData = subData(getPath(fullData, path))
            }
            return setIn(path, subData, fullData)
        })
        return new Store(subData, setter)
    }

    input<E=any>(pathStr: KeyOrPath<T>, valueGetter?: (ev: E) => ValueOf<T>) {
        const path = toPath(pathStr)
        return {
            onChange: (ev: E) => {
                // can this be simplified? https://github.com/lodash/lodash/issues/1696
                // https://gist.github.com/LukeChannings/15c92cef5a016a8b21a0 <-- doesn't look like it supports arrays
                // this.set(setIn(path,ev.target.value));
                this.set(path, valueGetter != null ? valueGetter(ev) : (ev as CompatEvent).target.value)
                // this.update(setSpec(path, ev.target.value))
            },
            value: getPath(this.data, path, ''),
        }
    }

    checkbox(pathStr: KeyOrPath<T>, valueGetter?: (ev: CheckboxEvent) => any) {
        const path = toPath(pathStr)
        return {
            onChange: (ev: CheckboxEvent) => {
                this.set(path, valueGetter != null ? valueGetter(ev) : ev.target.checked)
            },
            checked: toBool(getPath(this.data, path, false)),
        }
    }

    checkboxList(pathStr: KeyOrPath<T>) {
        return {
            onChange: (ev:AioCheckboxListChangeEvent<unknown>) => this.set(pathStr as Path, ev.values),
            values: this.get(pathStr as Path),
        }
    }

    checkboxSet<T = any>(pathStr: KeyOrPath<T>, value: T, valueGetter?: (ev: CheckboxEvent) => boolean) {
        const path = toPath(pathStr)
        const curr: Array<T> | Set<T> | undefined = getPath(this.data, path)
        let checked
        if(!curr) {
            checked = false
        } else if(Array.isArray(curr)) {
            checked = iterableIncludes(curr, value)
        } else {
            checked = curr.has(value)
        }
        return {
            onChange: (ev: CheckboxEvent) => {
                const checked = valueGetter != null ? valueGetter(ev) : ev.target.checked
                this.set(path, (set?: Set<T> | Array<T>) => arraySetChange(checked, set ?? new Set<T>(), value))
            },
            checked,
        }
    }
}

export function useStore<T extends Record<string, any> = Record<string, any>>(initialData: T | (() => T)) {
    const [data, setData] = useState<T>(initialData) // TODO: use useReducer instead??
    useDebugValue(data)
    return useMemo(() => new Store<T>(data, setData), [data, setData])
}
